import { useRef, useState } from 'react'
import { bridge } from '~/services/bridge'
import { BetterToast } from '~/utils/toast'

interface UploadInstructionsSlideProps {
  onInstructionsChange?: (instructions: string) => void
}

interface UploadedFile {
  name: string
  content: string
}

export function UploadInstructionsSlide({ onInstructionsChange }: UploadInstructionsSlideProps) {
  const isPlugin = bridge.isInPlugin()
  const [instructions, setInstructions] = useState('')
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Notify parent of instructions (file content takes priority over text)
  const notifyParent = (textInstructions: string, file: UploadedFile | null) => {
    // If file is uploaded, use file content; otherwise use text instructions
    const instructions = file ? file.content : textInstructions
    onInstructionsChange?.(instructions)
  }

  const handleChange = (value: string) => {
    setInstructions(value)
    notifyParent(value, uploadedFile)
  }

  const handleAttachClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    fileInputRef.current?.click()
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const newUploadedFile = { name: file.name, content: text }
      setUploadedFile(newUploadedFile)
      notifyParent(instructions, newUploadedFile)
    } catch (error) {
      console.error('Error reading file:', error)
      BetterToast.error('Failed to read file')
    }

    // Reset the input so the same file can be uploaded again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setUploadedFile(null)
    notifyParent(instructions, null)
  }

  return (
    <div className='w-full h-full overflow-y-auto'>
      <div className={`flex justify-center w-full ${isPlugin ? 'px-3' : 'px-[60px]'}`}>
        <div className={`w-full flex flex-col ${isPlugin ? 'max-w-full' : 'max-w-[720px]'}`}>
          {/* Modal Header */}
          <div className='flex flex-col gap-[16px] items-center pt-[60px]'>
            <p className="font-['Geist'] font-bold text-[20px] leading-[22px] text-black text-center">
              Upload instructions to guide AI refactoring (Optional)
            </p>
            <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] text-center">
              This is an optional step if you would like to upload a document or add text with
              specific instructions for the AI to follow during refactoring. Once uploaded/added the
              text, you begin the refactoring process.
            </p>
          </div>

          {/* Content */}
          <div className='flex flex-col gap-[36px] items-center w-full mt-[36px]'>
            <div className='flex flex-col gap-[16px] items-start w-full'>
              {/* Hidden file input - only plain text files */}
              <input
                ref={fileInputRef}
                type='file'
                accept='.txt,.md,.xml'
                onChange={handleFileUpload}
                className='hidden'
              />

              {/* Chat-like input box */}
              <div className='flex flex-col gap-[12px] items-start w-full'>
                <div
                  className={`border border-solid border-[rgba(91,91,213,0.2)] flex gap-[10px] items-center p-[16px] rounded-[24px] w-full shadow-[0px_2px_4px_0px_rgba(0,0,0,0.02),0px_1px_6px_-1px_rgba(0,0,0,0.02),0px_1px_2px_0px_rgba(0,0,0,0.03)] ${
                    uploadedFile ? 'bg-[#f5f5f5]' : 'bg-white'
                  }`}
                >
                  {/* Content area */}
                  <div className='flex-1 flex gap-[2px] items-center min-h-[22px]'>
                    {uploadedFile ? (
                      /* File uploaded state - show file icon, filename, and delete button */
                      <div className='flex-1 flex items-center justify-between overflow-clip px-[16px] py-[4px] rounded-[16px]'>
                        <div className='flex items-center gap-[16px]'>
                          {/* File Icon */}
                          <div className='flex items-center justify-center size-[24px]'>
                            <svg
                              width='18'
                              height='18'
                              viewBox='0 0 18 18'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M10.5 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6L10.5 1.5Z'
                                stroke='#000000'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                              <path
                                d='M10.5 1.5V6H15'
                                stroke='#000000'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                              <path
                                d='M6 9.75H12'
                                stroke='#000000'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                              <path
                                d='M6 12.75H12'
                                stroke='#000000'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </div>
                          {/* Filename */}
                          <span className="font-['Geist'] font-medium text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)] text-center whitespace-nowrap tracking-[0.1px]">
                            {uploadedFile.name}
                          </span>
                        </div>
                        {/* Delete Button */}
                        <button
                          type='button'
                          onClick={handleRemoveFile}
                          className='flex items-center justify-center size-[24px] hover:bg-[rgba(0,0,0,0.04)] rounded-[4px] transition-colors'
                          title='Remove file'
                        >
                          <svg
                            width='18'
                            height='18'
                            viewBox='0 0 18 18'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M2.25 4.5H15.75'
                              stroke='#000000'
                              strokeWidth='1.5'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                            <path
                              d='M14.25 4.5V15C14.25 15.3978 14.092 15.7794 13.8107 16.0607C13.5294 16.342 13.1478 16.5 12.75 16.5H5.25C4.85218 16.5 4.47064 16.342 4.18934 16.0607C3.90804 15.7794 3.75 15.3978 3.75 15V4.5'
                              stroke='#000000'
                              strokeWidth='1.5'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                            <path
                              d='M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5'
                              stroke='#000000'
                              strokeWidth='1.5'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      /* No file - show attach button and textarea */
                      <>
                        {/* Attach icon - clickable */}
                        <button
                          type='button'
                          onClick={handleAttachClick}
                          className='flex items-center justify-center shrink-0 w-[32px] h-[32px] rounded-[12px] cursor-pointer hover:bg-[rgba(0,0,0,0.05)] transition-colors'
                          title='Upload file'
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M14.2929 7.77472L8.16618 13.9014C7.41565 14.652 6.39766 15.0737 5.33621 15.0737C4.27475 15.0737 3.25677 14.652 2.50621 13.9014C1.75564 13.1509 1.33398 12.1329 1.33398 11.0714C1.33398 10.0099 1.75564 8.99199 2.50621 8.24139L8.63285 2.11473C9.13325 1.61436 9.81192 1.33325 10.5195 1.33325C11.2272 1.33325 11.9059 1.61436 12.4062 2.11473C12.9066 2.61511 13.1877 3.29377 13.1877 4.0014C13.1877 4.70904 12.9066 5.38769 12.4062 5.88807L6.27288 12.0147C6.02269 12.2649 5.68336 12.4055 5.32954 12.4055C4.97572 12.4055 4.6364 12.2649 4.38621 12.0147C4.13602 11.7645 3.99546 11.4253 3.99546 11.0714C3.99546 10.7176 4.13602 10.3783 4.38621 10.1281L10.0462 4.47473'
                              stroke='black'
                              strokeOpacity='0.25'
                              strokeWidth='1.5'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </button>
                        {/* Textarea field for multi-line content */}
                        <textarea
                          value={instructions}
                          onChange={e => handleChange(e.target.value)}
                          placeholder='Anything helpful for accurate output...'
                          rows={1}
                          className="flex-1 w-full bg-transparent border-none outline-none font-['Geist'] font-medium text-[14px] leading-[18px] text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.25)] resize-none overflow-hidden"
                          style={{
                            minHeight: '22px',
                            maxHeight: '200px',
                            paddingTop: '4px'
                          }}
                          onInput={e => {
                            const target = e.target as HTMLTextAreaElement
                            target.style.height = 'auto'
                            target.style.height = target.scrollHeight + 'px'
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
